import type { Node, Edge, ResolvedSchema } from './types';
import type { ElementDefinition } from 'cytoscape';

type CytoscapeStyle = { selector: string; style: Record<string, unknown> };

export function transformToElements(
  nodes: Node[],
  edges: Edge[],
  schema?: ResolvedSchema,
): { nodes: ElementDefinition[]; edges: ElementDefinition[] } {
  const cyNodes: ElementDefinition[] = nodes.map((node) => {
    const classes = [node.kind, node.layer].filter(Boolean);
    if (schema?.nodeKinds[node.kind]?.recursive) classes.push('recursive');
    if (schema && isImplementable(node, schema)) classes.push('implementable');
    return {
      data: { id: node.id, label: node.name || node.id, kind: node.kind, layer: node.layer, type: node.type },
      classes: classes.join(' '),
    };
  });

  const cyEdges: ElementDefinition[] = edges.map((edge, i) => ({
    data: {
      id: `edge-${i}-${edge.from}-${edge.to}`,
      source: edge.from,
      target: edge.to,
      label: edge.kind,
      kind: edge.kind,
      layer: edge.layer,
    },
    classes: [edge.kind, edge.layer].filter(Boolean).join(' '),
  }));

  return { nodes: cyNodes, edges: cyEdges };
}

function isImplementable(node: Node, schema: ResolvedSchema): boolean {
  if (typeof node.implementable === 'boolean') return node.implementable;
  return schema.nodeKinds[node.kind]?.implementable ?? false;
}

export function generateStyles(schema: ResolvedSchema): CytoscapeStyle[] {
  const styles: CytoscapeStyle[] = [
    {
      selector: 'node',
      style: {
        'label': 'data(label)',
        'text-valign': 'center',
        'text-halign': 'center',
        'text-wrap': 'wrap',
        'text-max-width': '100px',
        'font-size': '12px',
        'font-family': 'JetBrains Mono, monospace',
        'background-color': '#666666',
        'border-width': 2,
        'border-color': '#444444',
        'width': 80,
        'height': 80,
        'color': '#ffffff',
        'text-outline-width': 2,
        'text-outline-color': '#333333',
        'cursor': 'pointer',
      },
    },
    {
      selector: 'edge',
      style: {
        'label': 'data(label)',
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
        'source-arrow-shape': 'none',
        'line-color': '#888888',
        'font-size': '11px',
        'font-family': 'JetBrains Mono, monospace',
        'text-rotation': 'autorotate',
        'text-margin-y': -10,
        'color': '#d4d4d4',
        'width': 2,
        'text-outline-width': 2,
        'text-outline-color': '#1a1a1a',
        'text-background-color': '#1a1a1a',
        'text-background-opacity': 0.7,
        'text-background-padding': '3px',
        'text-background-shape': 'roundrectangle',
      },
    },
    {
      selector: 'node:selected',
      style: { 'border-width': 4, 'border-color': '#2196F3', 'background-color': '#1976D2' },
    },
    {
      selector: 'edge:selected',
      style: { 'line-color': '#2196F3', 'width': 4, 'color': '#ffffff' },
    },
    {
      selector: 'node.highlighted',
      style: { 'border-width': 3, 'border-color': '#FFC107' },
    },
    {
      selector: 'edge.highlighted',
      style: { 'width': 4, 'line-color': '#FFC107', 'opacity': 1, 'z-index': 10 },
    },
    {
      selector: 'node.neighbor-highlighted',
      style: { 'border-color': '#FFC107', 'border-width': 3, 'opacity': 1, 'z-index': 10 },
    },
    {
      selector: '.faded',
      style: { 'opacity': 0.15 },
    },
    {
      selector: 'node.recursive',
      style: { 'border-style': 'double', 'border-width': 6, 'border-opacity': 0.9 },
    },
    {
      selector: 'node.implementable',
      style: { 'border-style': 'dashed', 'border-width': 3, 'border-opacity': 0.9 },
    },
  ];

  // Node kind styles
  for (const [kindId, kindDef] of Object.entries(schema.nodeKinds)) {
    const shape = mapShape(kindDef.ui.shape);
    const color = kindDef.ui.color;
    styles.push({
      selector: `node.${kindId}`,
      style: { 'shape': shape, 'background-color': color, 'border-color': darkenColor(color) },
    });
  }

  // Edge kind styles
  for (const [kindId, kindDef] of Object.entries(schema.edgeKinds)) {
    styles.push({
      selector: `edge.${kindId}`,
      style: { 'line-color': kindDef.ui.color },
    });
  }

  return styles;
}

const SHAPE_MAP: Record<string, string> = {
  'ellipse': 'ellipse', 'round-rectangle': 'round-rectangle', 'rectangle': 'rectangle',
  'diamond': 'diamond', 'hexagon': 'hexagon', 'round-tag': 'tag', 'tag': 'tag',
  'octagon': 'octagon', 'star': 'star', 'triangle': 'triangle', 'vee': 'vee',
  'barrel': 'barrel', 'rhomboid': 'rhomboid', 'polygon': 'polygon',
};

function mapShape(shape: string): string {
  return SHAPE_MAP[shape] || 'ellipse';
}

function darkenColor(hex: string): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = Math.max(0, parseInt(m[1], 16) - 40);
  const g = Math.max(0, parseInt(m[2], 16) - 40);
  const b = Math.max(0, parseInt(m[3], 16) - 40);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
